import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, Search, Filter, Calendar, Clock, User as UserIcon, CheckCircle, ChevronDown, Loader2, RefreshCw, Maximize2, FileText, Mail, Settings, LayoutGrid, List, MoreHorizontal } from 'lucide-react';
import FormioRenderer from '../components/formio-custom/FormioRenderer';
import Sidebar from '../components/ui/Sidebar';
import { toast } from 'sonner';
import { useAuthContext } from '../../components/auth/AuthProvider';
import { getCamundaService } from '../../infrastructure/camunda';
import { getUserService, type User } from '../../services/userService';
import { getTaskFormInfo, getDesignerFormContent, parseFormioJson } from '../../services/taskService';
import type { Task, Attachment } from '../../infrastructure/camunda';
// FormIO runtime — formiojs v4 (legacy). We swapped off
// `@formio/react@6` + `@formio/js@5` because the FormBuilder
// component-edit dialog froze on every interaction and the same v5
// renderer is what powered <Form> here. `FormioRenderer` is a thin
// DOM-ref wrapper around `Formio.createForm` — no React reactivity
// inside the FormIO subtree.

interface TaskItem {
  id: string;
  name: string;
  description: string;
  assignee?: string;
  assigneeAvatar?: string;
  created: string;
  due: string;
  priority: 'high' | 'medium' | 'low';
  status: 'unassigned' | 'assigned' | 'completed';
  processName: string;
  processInstanceId: string;
  processDefinitionId: string;
  formKey?: string;
  tenantId?: string;
}

interface TaskContext {
  businessKey: string;
  processDefinitionId: string;
  processInstanceId: string;
  caseInstanceId: string;
  caseExecutionId: string;
  caseDefinitionId: string;
  suspended: boolean;
  formKey: string;
  tenantId: string;
}

interface AttachedFile {
  id: string;
  name: string;
  type: string;
  size: string;
}

interface ContextVariable {
  key: string;
  value: string;
}

const TasksPage: React.FC = () => {
  const [selectedTask, setSelectedTask] = useState<TaskItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('24 hours');
  const [showFilters, setShowFilters] = useState(false);
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [taskContext, setTaskContext] = useState<TaskContext | null>(null);
  const [completing, setCompleting] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [decision, setDecision] = useState('');
  const [comments, setComments] = useState('');
  const [showFullDialog, setShowFullDialog] = useState(false);
  // taskContextExpanded removed — no longer used in mailbox layout
  const [formSchema, setFormSchema] = useState<Record<string, unknown> | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isFormioForm, setIsFormioForm] = useState(false);
  const [camundaFormHtml, setCamundaFormHtml] = useState<string | null>(null);
  const [contextVariables, setContextVariables] = useState<ContextVariable[]>([]);

  // Task management state
  const [orgUsers, setOrgUsers] = useState<User[]>([]);
  const [editPriority, setEditPriority] = useState<number>(50);
  const [editDueDate, setEditDueDate] = useState<string>('');
  const [editAssignee, setEditAssignee] = useState<string>('');
  const [savingTask, setSavingTask] = useState(false);
  const [showTaskActions, setShowTaskActions] = useState(false);

  // Ref for Formio form to trigger submit programmatically
  const formioRef = useRef<any>(null);

  // View mode toggle (list/board)
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');

  const { user } = useAuthContext();
  const camunda = getCamundaService();

  // Convert context variables to Formio submission format (flat structure for form preset)
  const formSubmission = useMemo(() => {
    const submission: Record<string, any> = {};

    contextVariables.forEach((variable) => {
      // ... same parsing logic as before, just ensuring type safety ...
      let value: any = variable.value;
      try { value = JSON.parse(variable.value); } catch { }
      // Basic type conversions if not JSON
      if (variable.value === 'true') value = true;
      else if (variable.value === 'false') value = false;
      else if (/^\d+$/.test(variable.value)) value = parseInt(variable.value, 10);
      else if (/^\d*\.\d+$/.test(variable.value)) value = parseFloat(variable.value);

      submission[variable.key] = value;
    });

    return { data: submission };
  }, [contextVariables]);

  // Format date relative to now
  const formatRelativeTime = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  const formatDueDate = (dateString: string | null): string => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'tomorrow';
    return `in ${diffDays} days`;
  };

  // Pagination state
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(0);

  // Fetch tasks from Camunda with pagination
  const fetchTasks = useCallback(async (page: number = 0) => {
    try {
      setLoading(true);

      // Get total count first
      const countResult = await camunda.task.getCount({});
      setTotalCount(countResult.count);

      // Fetch paginated tasks
      const camundaTasks = await camunda.task.getList({
        sortBy: 'created',
        sortOrder: 'desc',
        firstResult: page * pageSize,
        maxResults: pageSize,
      });

      const taskItems: TaskItem[] = camundaTasks.map((task: Task) => {
        const priority: TaskItem['priority'] =
          task.priority >= 75 ? 'high' :
            task.priority >= 50 ? 'medium' : 'low';

        const status: TaskItem['status'] =
          task.assignee ? 'assigned' : 'unassigned';

        return {
          id: task.id,
          name: task.name || 'Unnamed Task',
          description: task.description || task.processDefinitionId || '',
          assignee: task.assignee || undefined,
          created: formatRelativeTime(task.created),
          due: formatDueDate(task.due),
          priority,
          status,
          processName: task.processDefinitionId?.split(':')[0] || 'Unknown Process',
          processInstanceId: task.processInstanceId || '',
          processDefinitionId: task.processDefinitionId || '',
          formKey: task.formKey || undefined,
          tenantId: task.tenantId || undefined,
        };
      });

      setTasks(taskItems);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  }, [camunda]);

  // Fetch form info from Camunda and load Form.io form if available
  const fetchTaskForm = useCallback(async (taskId: string) => {
    // RESET ALL FORM STATES IMMEDIATELY
    setFormLoading(true);
    setFormSchema(null);
    setIsFormioForm(false);
    setCamundaFormHtml(null);
    setAttachedFiles([]);
    setContextVariables([]);
    setTaskContext(null);

    try {
      // Step 1: Get form info from Camunda using task service
      const formInfo = await getTaskFormInfo(taskId);

      if (formInfo?.key) {
        // Check if it's our Formio form pattern (api/form-contents/...)
        if (formInfo.key.startsWith('api/form-contents/')) {
          // Step 2: If key exists, fetch from Designer API using task service
          const designerResponse = await getDesignerFormContent(formInfo.key);

          if (designerResponse.formioJson) {
            // Decode base64 formioJson using task service
            const schema = parseFormioJson(designerResponse.formioJson);
            setFormSchema(schema);
            setIsFormioForm(true);
            return;
          }
        }

        // Try to get Camunda rendered form
        try {
          const renderedFormHtml = await camunda.task.getRenderedForm(taskId);
          if (renderedFormHtml) {
            setCamundaFormHtml(renderedFormHtml);
            return;
          }
        } catch (renderedErr) {
          console.log('No rendered form available, trying deployed form');
        }

        // Try to get Camunda deployed form
        try {
          const deployedFormHtml = await camunda.task.getDeployedForm(taskId);
          if (deployedFormHtml) {
            setCamundaFormHtml(deployedFormHtml);
            return;
          }
        } catch (deployedErr) {
          console.log('No deployed form available');
        }
      }
    } catch (err: any) {
      console.error('Error loading form:', err);
      toast.error(err.message || 'Failed to load form');
    } finally {
      setFormLoading(false);
    }
  }, [camunda]);

  // Fetch task details when selected
  const fetchTaskDetails = useCallback(async (taskId: string, task: TaskItem) => {
    try {
      // Step 1: Fetch form first (so we know what fields to populate)
      await fetchTaskForm(taskId);

      // Step 2: Fetch attachments and variables in parallel
      const [attachments, variables] = await Promise.all([
        camunda.task.getAttachments(taskId).catch(() => []),
        camunda.task.getVariables(taskId).catch(() => ({})),
      ]);

      // Process attachments
      const files: AttachedFile[] = attachments.map((att: Attachment) => ({
        id: att.id,
        name: att.name || 'Unknown',
        type: att.type || 'File',
        size: 'N/A',
      }));
      setAttachedFiles(files);

      // Process variables - these will auto-populate form via formSubmission useMemo
      const contextVars: ContextVariable[] = Object.entries(variables).map(([key, val]: [string, any]) => ({
        key,
        value: typeof val.value === 'object' ? JSON.stringify(val.value) : String(val.value ?? ''),
      }));
      setContextVariables(contextVars);

      // Set task context
      setTaskContext({
        businessKey: '',
        processDefinitionId: task.processDefinitionId,
        processInstanceId: task.processInstanceId,
        caseInstanceId: '',
        caseExecutionId: '',
        caseDefinitionId: '',
        suspended: false,
        formKey: task.formKey || '',
        tenantId: task.tenantId || '',
      });
    } catch (error) {
      console.error('Error fetching task details:', error);
      setAttachedFiles([]);
      setContextVariables([]);
    }
  }, [camunda, fetchTaskForm]);

  // Claim a task
  const handleClaimTask = async () => {
    if (!selectedTask) return;
    try {
      setClaiming(true);
      // Get current user from localStorage token
      const tokenData = localStorage.getItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
      let userId = 'current-user';
      if (tokenData) {
        try {
          const parsed = JSON.parse(tokenData);
          userId = parsed.profile?.preferred_username || parsed.profile?.email || 'current-user';
        } catch { }
      }

      await camunda.task.claim(selectedTask.id, userId);
      toast.success('Task claimed successfully');
      fetchTasks();
      setSelectedTask(null);
    } catch (error) {
      console.error('Error claiming task:', error);
      toast.error('Failed to claim task');
    } finally {
      setClaiming(false);
    }
  };

  // Complete a task
  const handleCompleteTask = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedTask) return;

    try {
      setCompleting(true);
      const completedTaskId = selectedTask.id;
      await camunda.task.complete(selectedTask.id, {
        variables: {
          decision: { value: decision, type: 'String' },
          comments: { value: comments, type: 'String' },
        },
      });
      toast.success('Task completed successfully');
      setDecision('');
      setComments('');
      setShowFullDialog(false);

      // Find the next task to select
      const currentIndex = tasks.findIndex(t => t.id === completedTaskId);
      const nextTask = tasks[currentIndex + 1] || tasks[currentIndex - 1] || null;

      await fetchTasks(currentPage);
      // If fetchTasks updates tasks state, pick the next available
      if (nextTask) {
        setSelectedTask(nextTask);
      } else {
        setSelectedTask(null);
      }
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    } finally {
      setCompleting(false);
    }
  };

  // Handle Form.io form submission
  const handleFormioSubmit = async (formData: Record<string, unknown>) => {
    if (!selectedTask) return;

    try {
      setCompleting(true);
      const completedTaskId = selectedTask.id;

      // Convert form data to Camunda variables format
      const variables: Record<string, { value: unknown }> = {};
      Object.keys(formData).forEach(key => {
        if (key !== 'submit') {
          variables[key] = { value: formData[key] };
        }
      });

      await camunda.task.complete(selectedTask.id, { variables });
      toast.success('Task completed successfully');
      setShowFullDialog(false);

      // Find the next task to select
      const currentIndex = tasks.findIndex(t => t.id === completedTaskId);
      const nextTask = tasks[currentIndex + 1] || tasks[currentIndex - 1] || null;

      await fetchTasks(currentPage);
      if (nextTask) {
        setSelectedTask(nextTask);
      } else {
        setSelectedTask(null);
      }
    } catch (error) {
      console.error('Error completing task with form data:', error);
      toast.error('Failed to complete task');
    } finally {
      setCompleting(false);
    }
  };

  // Trigger Formio form submit programmatically when Complete button is clicked
  const handleCompleteWithFormio = () => {
    if (isFormioForm && formioRef.current) {
      // Formio form instance has a submit() method
      formioRef.current.formio?.submit();
    } else {
      // Fallback to regular complete if no formio form
      handleCompleteTask();
    }
  };

  // Fetch org users for assignment dropdown
  const fetchOrgUsers = useCallback(async () => {
    try {
      // Use a default orgId or get from user context if available
      const orgId = user?.profile?.org_id || 'default';
      const userService = getUserService();
      const response = await userService.getUsers(orgId, '', 0, 100);
      setOrgUsers(response.content || []);
    } catch (error) {
      console.error('Error fetching org users:', error);
      // Silently fail - users dropdown will just be empty
    }
  }, [user]);

  // Update task properties (priority, due date, assignee)
  const handleUpdateTask = async () => {
    if (!selectedTask) return;

    try {
      setSavingTask(true);

      // Camunda PUT API replaces ALL fields, so we MUST include name and description
      // to prevent them from being set to null
      const updateDto: {
        name?: string;
        description?: string;
        priority?: number;
        due?: string;
        assignee?: string;
      } = {
        name: selectedTask.name, // Always include to preserve
        description: selectedTask.description || undefined, // Preserve description
      };

      // Include changed values
      const currentPriority = selectedTask.priority === 'high' ? 75 : selectedTask.priority === 'medium' ? 50 : 25;
      if (editPriority !== currentPriority) {
        updateDto.priority = editPriority;
      } else {
        updateDto.priority = currentPriority; // Always include current priority
      }

      let newDue = selectedTask.due;
      if (editDueDate) {
        // Camunda expects format: yyyy-MM-dd'T'HH:mm:ss.SSS+0000 (not trailing Z)
        const date = new Date(editDueDate);
        updateDto.due = date.toISOString().replace('Z', '+0000');
        newDue = formatDueDate(editDueDate);
      }

      if (editAssignee && editAssignee !== selectedTask.assignee) {
        updateDto.assignee = editAssignee;
      } else if (selectedTask.assignee) {
        updateDto.assignee = selectedTask.assignee; // Preserve current assignee
      }

      // Always call API with full DTO
      await camunda.task.update(selectedTask.id, updateDto);
      toast.success('Task updated successfully');

      // Update local state first (preserve all existing properties)
      const updatedTask: TaskItem = {
        ...selectedTask,
        priority: editPriority >= 75 ? 'high' : editPriority >= 50 ? 'medium' : 'low',
        assignee: editAssignee || selectedTask.assignee,
        status: editAssignee ? 'assigned' : selectedTask.status,
        due: newDue,
      };
      setSelectedTask(updatedTask);

      // Then refresh the list in background
      fetchTasks(currentPage);
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    } finally {
      setSavingTask(false);
    }
  };

  // Initialize edit values when task is selected
  useEffect(() => {
    if (selectedTask) {
      setEditPriority(
        selectedTask.priority === 'high' ? 75 :
          selectedTask.priority === 'medium' ? 50 : 25
      );
      setEditAssignee(selectedTask.assignee || '');
      setEditDueDate('');
      setShowTaskActions(false);
    }
  }, [selectedTask]);

  // Fetch org users when modal opens
  useEffect(() => {
    if (showFullDialog) {
      fetchOrgUsers();
    }
  }, [showFullDialog, fetchOrgUsers]);

  // Initial load and page change
  useEffect(() => {
    fetchTasks(currentPage);
  }, [fetchTasks, currentPage]);

  // Load task details when selected
  useEffect(() => {
    if (selectedTask) {
      fetchTaskDetails(selectedTask.id, selectedTask);
    }
  }, [selectedTask, fetchTaskDetails]);

  // Filter tasks by search query (client-side filter on currently loaded page)
  const filteredTasks = searchQuery
    ? tasks.filter(task =>
      task.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.processName.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : tasks;

  const getPriorityColor = (priority: TaskItem['priority']) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  /*
    const getStatusIcon = (status: TaskItem['status']) => {
      switch (status) {
        case 'assigned':
          return <UserIcon size={16} className="text-blue-600" />;
        case 'completed':
          return <CheckCircle size={16} className="text-green-600" />;
        case 'unassigned':
          return <AlertCircle size={16} className="text-orange-600" />;
        default:
          return null;
      }
    };
  */

  const getStatusColor = (status: TaskItem['status']) => {
    switch (status) {
      case 'assigned':
        return 'bg-blue-100 text-blue-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'unassigned':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 ml-16 mr-4 mt-4 mb-4">
        <div className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
          <header className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle size={20} className="text-green-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Tasks</h1>
                  <p className="text-sm text-gray-500">Claim and complete your tasks</p>
                </div>
                {/* View Toggle */}
                <div className="flex items-center gap-1 ml-4 border-l border-gray-200 pl-4">
                  <button
                    onClick={() => setViewMode('board')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'board'
                      ? 'text-green-600 bg-green-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <LayoutGrid size={16} />
                    Board
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'list'
                      ? 'text-green-600 bg-green-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                  >
                    <List size={16} />
                    List
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 w-64 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                  />
                </div>
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  {['24h', '7d', '30d'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setSelectedTimeFilter(filter)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${selectedTimeFilter === filter
                        ? 'bg-white text-green-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                >
                  <Filter size={14} className="text-gray-500" />
                  <span className="text-gray-600">Filter</span>
                  <ChevronDown size={14} className={`text-gray-400 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
                <button
                  onClick={() => fetchTasks()}
                  className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  disabled={loading}
                >
                  <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>
            {/* Filter Panel */}
            {showFilters && (
              <div className="border-t border-gray-200 pt-4">
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option>All Statuses</option>
                      <option>Unassigned</option>
                      <option>Assigned</option>
                      <option>Completed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option>All Priorities</option>
                      <option>High</option>
                      <option>Medium</option>
                      <option>Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assignee</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option>All Assignees</option>
                      <option>Unassigned</option>
                      <option>john.doe@example.com</option>
                      <option>sarah.wilson@example.com</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Process</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option>All Processes</option>
                      <option>Invoice Processing</option>
                      <option>Customer Onboarding</option>
                      <option>Document Check Handling</option>
                      <option>Order Confirmation</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </header>

          <div className="flex flex-1 overflow-hidden">
            {/* Left Panel - Task List (compact mailbox) */}
            <div className="w-[280px] flex-shrink-0 border-r border-gray-200 flex flex-col bg-white">

              {/* Conditional View Rendering */}
              {viewMode === 'board' ? (
                /* Kanban Board View */
                <div className="flex gap-4 h-[calc(100vh-280px)] overflow-x-auto pb-4">
                  {/* Column: Unassigned */}
                  <div className="flex-shrink-0 w-80 bg-gray-50 rounded-xl flex flex-col">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        <h3 className="font-medium text-gray-900">Unassigned</h3>
                        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                          {filteredTasks.filter(t => !t.assignee).length}
                        </span>
                      </div>
                      <button className="text-gray-400 hover:text-gray-600">
                        <MoreHorizontal size={16} />
                      </button>
                    </div>
                    <div className="flex-1 p-3 space-y-3 overflow-y-auto">
                      {filteredTasks.filter(t => !t.assignee).map(task => (
                        <div
                          key={task.id}
                          onClick={() => setSelectedTask(task)}
                          className={`bg-white rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md ${selectedTask?.id === task.id ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200'
                            }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className={`text-xs font-medium ${task.priority === 'high' ? 'text-red-600' :
                              task.priority === 'medium' ? 'text-yellow-600' : 'text-blue-600'
                              }`}>
                              • {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
                            </span>
                            <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">v124</span>
                          </div>
                          <p className="text-sm text-gray-800 line-clamp-2 mb-3">{task.name}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-green-600 font-medium">{task.processName || 'Process'}</span>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <Calendar size={12} />
                              {task.created}
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                            <span>Assign</span>
                            <div className="flex items-center gap-2">
                              <FileText size={12} />
                              <span>0</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Column: In Progress */}
                  <div className="flex-shrink-0 w-80 bg-gray-50 rounded-xl flex flex-col">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <h3 className="font-medium text-gray-900">In Progress</h3>
                        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                          {filteredTasks.filter(t => t.assignee && t.status !== 'completed').length}
                        </span>
                      </div>
                      <button className="text-gray-400 hover:text-gray-600">
                        <MoreHorizontal size={16} />
                      </button>
                    </div>
                    <div className="flex-1 p-3 space-y-3 overflow-y-auto">
                      {filteredTasks.filter(t => t.assignee && t.status !== 'completed').map(task => (
                        <div
                          key={task.id}
                          onClick={() => setSelectedTask(task)}
                          className={`bg-white rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md ${selectedTask?.id === task.id ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200'
                            }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className={`text-xs font-medium ${task.priority === 'high' ? 'text-red-600' :
                              task.priority === 'medium' ? 'text-yellow-600' : 'text-blue-600'
                              }`}>
                              • {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
                            </span>
                            <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">v124</span>
                          </div>
                          <p className="text-sm text-gray-800 line-clamp-2 mb-3">{task.name}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-green-600 font-medium">{task.processName || 'Process'}</span>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <Calendar size={12} />
                              {task.created}
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-[10px]">
                                {task.assignee?.charAt(0).toUpperCase() || '?'}
                              </div>
                              <span className="truncate max-w-[100px]">{task.assignee}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FileText size={12} />
                              <span>0</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Column: Completed */}
                  <div className="flex-shrink-0 w-80 bg-gray-50 rounded-xl flex flex-col">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <h3 className="font-medium text-gray-900">Completed</h3>
                        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                          {filteredTasks.filter(t => t.status === 'completed').length}
                        </span>
                      </div>
                      <button className="text-gray-400 hover:text-gray-600">
                        <MoreHorizontal size={16} />
                      </button>
                    </div>
                    <div className="flex-1 p-3 space-y-3 overflow-y-auto">
                      {filteredTasks.filter(t => t.status === 'completed').length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                          <CheckCircle size={32} className="mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No completed tasks</p>
                        </div>
                      ) : (
                        filteredTasks.filter(t => t.status === 'completed').map(task => (
                          <div
                            key={task.id}
                            onClick={() => setSelectedTask(task)}
                            className={`bg-white rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md opacity-75 ${selectedTask?.id === task.id ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200'
                              }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <span className="text-xs font-medium text-green-600">
                                <CheckCircle size={12} className="inline mr-1" />
                                Completed
                              </span>
                            </div>
                            <p className="text-sm text-gray-800 line-clamp-2 mb-3">{task.name}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-green-600 font-medium">{task.processName || 'Process'}</span>
                              <div className="flex items-center gap-2 text-xs text-gray-400">
                                <Calendar size={12} />
                                {task.created}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                /* List View - Ultra Compact */
                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                        <div key={i} className="px-3 py-2.5 border-b border-gray-50 animate-pulse">
                          <div className="h-3 bg-gray-100 rounded w-3/4 mb-1.5"></div>
                          <div className="h-2.5 bg-gray-50 rounded w-1/2"></div>
                        </div>
                      ))}
                    </>
                  ) : filteredTasks.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-6 text-gray-400">
                      <CheckCircle size={24} className="mb-2 opacity-40" />
                      <p className="text-xs">No tasks found</p>
                    </div>
                  ) : (
                    filteredTasks.map((task) => (
                      <div
                        key={task.id}
                        className={`px-4 py-3 border-b cursor-pointer transition-colors duration-150 ${selectedTask?.id === task.id
                          ? 'bg-green-50 border-l-[3px] border-l-green-600 border-b-green-100 shadow-[inset_0_0_8px_rgba(34,197,94,0.05)]'
                          : 'hover:bg-gray-50 border-l-[3px] border-l-transparent border-b-gray-50'
                          }`}
                        onClick={() => setSelectedTask(task)}
                      >
                        <div className="flex items-start gap-2.5">
                          <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full mt-1.5 ${task.priority === 'high' ? 'bg-red-500' : task.priority === 'medium' ? 'bg-yellow-400' : 'bg-green-400'}`}></span>
                          <div className="flex-1 min-w-0">
                            <h3 className={`text-[13px] leading-tight truncate ${selectedTask?.id === task.id ? 'font-bold text-gray-900' : 'font-semibold text-gray-700'}`}>{task.name}</h3>
                            <p className="text-[10px] text-gray-400 truncate mt-0.5">{task.processName}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-[10px] text-gray-400 flex items-center gap-1.5 ml-auto font-medium">
                                {task.created}
                                <Calendar size={11} className="text-gray-300" />
                              </span>
                              {task.assignee && (
                                <div className="flex items-center gap-1 text-[9px] font-semibold text-gray-400 bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded shadow-sm">
                                  <span className="opacity-60 text-[8px] uppercase">v0.2</span>
                                  <span className="border-l border-gray-100 pl-1">{task.assignee.split('@')[0]}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Pagination - Compact */}
              {viewMode === 'list' && (
                <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 flex-shrink-0 bg-gray-50/50">
                  <span className="text-[11px] text-gray-400">
                    {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0}
                      className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      ‹
                    </button>
                    <span className="text-xs text-gray-500 px-1">{currentPage + 1}/{totalPages || 1}</span>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                      disabled={currentPage >= totalPages - 1}
                      className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right Panel - Form Preview (mailbox style) */}
            <div key={selectedTask?.id || 'none'} className="flex-1 flex flex-col bg-gray-50/30 min-w-0">
              {selectedTask ? (
                <>
                  {/* Task Header Bar */}
                  <div className="px-6 py-3 bg-white border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <h2 className="text-base font-semibold text-gray-900 truncate">{selectedTask.name}</h2>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium ${getPriorityColor(selectedTask.priority)}`}>
                        {selectedTask.priority.charAt(0).toUpperCase() + selectedTask.priority.slice(1)}
                      </span>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium ${getStatusColor(selectedTask.status)}`}>
                        {selectedTask.status.charAt(0).toUpperCase() + selectedTask.status.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="flex items-center gap-3 text-xs text-gray-400 mr-3">
                        <span className="flex items-center gap-1"><Calendar size={12} />{selectedTask.created}</span>
                        <span className="flex items-center gap-1"><Clock size={12} />Due {selectedTask.due}</span>
                        {selectedTask.assignee && (
                          <span className="flex items-center gap-1"><UserIcon size={12} />{selectedTask.assignee}</span>
                        )}
                      </div>
                      <button
                        onClick={() => setShowFullDialog(true)}
                        className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Full screen"
                      >
                        <Maximize2 size={15} />
                      </button>
                      <button
                        onClick={() => setSelectedTask(null)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <X size={15} />
                      </button>
                    </div>
                  </div>

                  {/* Process Info Bar */}
                  <div className="px-6 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-4 text-xs text-gray-500 flex-shrink-0">
                    <span><strong className="text-gray-600">Process:</strong> {selectedTask.processName}</span>
                    <span><strong className="text-gray-600">Instance:</strong> {selectedTask.processInstanceId}</span>
                    {taskContext?.formKey && (
                      <span><strong className="text-gray-600">Form:</strong> {taskContext.formKey.split('/').pop()}</span>
                    )}
                  </div>

                  {/* Form / Content Area */}
                  <div className="flex-1 overflow-y-auto p-6">
                    <style>{`
                      .task-form-container .formio-component { margin-bottom: 14px; }
                      .task-form-container .formio-component label.col-form-label {
                        font-weight: 600; font-size: 13px; color: #374151; margin-bottom: 4px; padding: 0;
                      }
                      .task-form-container .form-control {
                        border: 1px solid #d1d5db; border-radius: 8px; padding: 10px 14px; font-size: 14px;
                        transition: border-color 0.15s, box-shadow 0.15s; background: #f9fafb;
                      }
                      .task-form-container .form-control:focus {
                        border-color: #22c55e; box-shadow: 0 0 0 3px rgba(34,197,94,0.15); background: #fff; outline: none;
                      }
                      .task-form-container .form-check { padding-left: 0; }
                      .task-form-container .form-check-input { margin-right: 8px; }
                      .task-form-container select.form-control { appearance: auto; }
                      .task-form-container textarea.form-control { min-height: 80px; resize: vertical; }
                      .task-form-container .btn-primary, .task-form-container .btn-wizard-nav-submit {
                        background: linear-gradient(135deg, #22c55e, #16a34a); border: none; border-radius: 8px;
                        padding: 10px 24px; font-weight: 600; font-size: 14px;
                        box-shadow: 0 1px 3px rgba(22,163,74,0.2); transition: all 0.15s;
                      }
                      .task-form-container .btn-primary:hover, .task-form-container .btn-wizard-nav-submit:hover {
                        background: linear-gradient(135deg, #16a34a, #15803d);
                        box-shadow: 0 2px 8px rgba(22,163,74,0.3); transform: translateY(-1px);
                      }
                      .task-form-container .btn-secondary, .task-form-container .btn-wizard-nav-previous {
                        background: #f3f4f6; color: #374151; border: 1px solid #d1d5db; border-radius: 8px;
                        padding: 10px 24px; font-weight: 500; font-size: 14px; transition: all 0.15s;
                      }
                      .task-form-container .btn-secondary:hover, .task-form-container .btn-wizard-nav-previous:hover { background: #e5e7eb; }
                      .task-form-container .btn-danger { border-radius: 8px; }
                      .task-form-container .formio-component-submit .btn { width: 100%; padding: 12px 24px; }
                      .task-form-container .card { border: 1px solid #e5e7eb; border-radius: 12px; box-shadow: none; }
                      .task-form-container .card-header { background: #f9fafb; border-bottom: 1px solid #e5e7eb; border-radius: 12px 12px 0 0 !important; padding: 12px 16px; font-weight: 600; font-size: 14px; }
                      .task-form-container .card-body { padding: 16px; }
                      .task-form-container .has-error .form-control { border-color: #ef4444; }
                      .task-form-container .formio-errors .error { color: #ef4444; font-size: 12px; margin-top: 4px; }
                      .task-form-container .help-block { font-size: 12px; color: #6b7280; margin-top: 4px; }
                      .task-form-container .formio-wizard-nav-container { display: flex; gap: 8px; justify-content: flex-end; padding-top: 16px; border-top: 1px solid #e5e7eb; margin-top: 16px; }
                      .task-form-container .alert { border-radius: 8px; font-size: 13px; }
                      .task-form-container .table { font-size: 13px; }
                    `}</style>
                    {formLoading ? (
                      <div className="flex flex-col items-center justify-center py-16 gap-3">
                        <Loader2 size={28} className="animate-spin text-green-500" />
                        <span className="text-sm text-gray-400">Loading form...</span>
                      </div>
                    ) : isFormioForm && formSchema ? (
                      <div className="task-form-container bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                        <FormioRenderer
                          form={formSchema}
                          submission={formSubmission}
                          onSubmit={(submission) => {
                            handleFormioSubmit(submission.data as Record<string, unknown>);
                          }}
                          formReady={(form: any) => {
                            // Match the prior shape so handleCompleteWithFormio
                            // (which calls `formioRef.current.formio?.submit()`)
                            // still works.
                            formioRef.current = { formio: form };
                          }}
                          options={{
                            readOnly: false,
                            noAlerts: true,
                          }}
                        />
                      </div>
                    ) : camundaFormHtml ? (
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                        <div
                          dangerouslySetInnerHTML={{ __html: camundaFormHtml }}
                          className="camunda-form [&_input]:w-full [&_input]:px-3 [&_input]:py-2 [&_input]:border [&_input]:border-gray-300 [&_input]:rounded-lg [&_input]:text-sm [&_input]:focus:outline-none [&_input]:focus:ring-2 [&_input]:focus:ring-green-500 [&_select]:w-full [&_select]:px-3 [&_select]:py-2 [&_select]:border [&_select]:border-gray-300 [&_select]:rounded-lg [&_select]:text-sm [&_select]:bg-white [&_textarea]:w-full [&_textarea]:px-3 [&_textarea]:py-2 [&_textarea]:border [&_textarea]:border-gray-300 [&_textarea]:rounded-lg [&_textarea]:text-sm [&_label]:block [&_label]:text-sm [&_label]:font-medium [&_label]:text-gray-700 [&_label]:mb-1 [&_.form-group]:mb-4"
                        />
                      </div>
                    ) : (
                      /* No Form - Default Task View */
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                        <div className="grid grid-cols-2 gap-6 mb-6">
                          <div>
                            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Description</h4>
                            <p className="text-sm text-gray-700">{selectedTask.description || 'No description available'}</p>
                          </div>
                          <div>
                            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Details</h4>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                <Calendar size={14} className="text-gray-400" />
                                <span className="text-gray-500">Due:</span>
                                <span className="text-gray-900 font-medium">{selectedTask.due}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Clock size={14} className="text-gray-400" />
                                <span className="text-gray-500">Created:</span>
                                <span className="text-gray-900">{selectedTask.created}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Context Variables */}
                        {contextVariables.length > 0 && (
                          <div className="border-t border-gray-100 pt-4 mt-4">
                            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Context Variables</h4>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                              {contextVariables.slice(0, 8).map((variable) => (
                                <div key={variable.key} className="flex items-center justify-between text-sm py-1">
                                  <span className="text-gray-500 truncate mr-2">{variable.key}:</span>
                                  <span className="text-gray-900 font-medium truncate max-w-[200px]" title={variable.value}>{variable.value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Attached Files */}
                        {attachedFiles.length > 0 && (
                          <div className="border-t border-gray-100 pt-4 mt-4">
                            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Attachments</h4>
                            <div className="grid grid-cols-2 gap-3">
                              {attachedFiles.map((file) => (
                                <div key={file.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                  <FileText size={16} className="text-gray-400 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-blue-600 truncate">{file.name}</p>
                                    <p className="text-xs text-gray-400">{file.type} • {file.size}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="border-t border-gray-100 pt-4 mt-4 text-center">
                          <p className="text-xs text-gray-400">No form is associated with this task. Use the action buttons below to complete it.</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer Actions */}
                  <div className="px-6 py-3 border-t border-gray-200 flex-shrink-0 bg-white">
                    <div className="flex gap-3">
                      {!selectedTask.assignee && (
                        <button
                          type="button"
                          onClick={handleClaimTask}
                          disabled={claiming}
                          className="flex items-center justify-center gap-2 px-5 py-2.5 border border-green-600 text-green-600 rounded-lg text-sm font-medium hover:bg-green-50 disabled:opacity-50 transition-colors"
                        >
                          {claiming && <Loader2 size={14} className="animate-spin" />}
                          Claim Task
                        </button>
                      )}
                      <div className="flex-1" />
                      <button
                        type="button"
                        onClick={() => setSelectedTask(null)}
                        className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleCompleteWithFormio}
                        disabled={completing}
                        className="flex items-center justify-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors shadow-sm"
                      >
                        {completing && <Loader2 size={14} className="animate-spin" />}
                        <CheckCircle size={15} />
                        Complete Task
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                /* No Task Selected - Empty State */
                <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <Mail size={28} className="text-gray-300" />
                  </div>
                  <h3 className="text-base font-medium text-gray-500 mb-1">Select a task</h3>
                  <p className="text-sm text-gray-400">Choose a task from the list to view details and forms</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full Screen Dialog */}
      {showFullDialog && selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowFullDialog(false)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Dialog Content - Redesigned */}
            {/* Dialog Header with Details */}
            <div className="p-6 border-b border-gray-200 bg-gray-50/50">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedTask.name}</h2>
                  <p className="text-sm text-gray-500 mt-1">{selectedTask.processName} • {selectedTask.id}</p>
                </div>
                <button
                  onClick={() => setShowFullDialog(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Task Metadata Header Bar */}
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${selectedTask.priority === 'high' ? 'bg-red-50 text-red-700 border-red-100' :
                    selectedTask.priority === 'medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' :
                      'bg-green-50 text-green-700 border-green-100'
                    }`}>
                    {selectedTask.priority.toUpperCase()} Priority
                  </span>
                </div>

                <div className="flex items-center gap-2 text-gray-600">
                  <Clock size={16} className="text-gray-400" />
                  <span>Created {selectedTask.created}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar size={16} className="text-gray-400" />
                  <span>Due {selectedTask.due}</span>
                </div>

                {selectedTask.assignee && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <UserIcon size={16} className="text-gray-400" />
                    <span>Assigned to <span className="font-medium text-gray-900">{selectedTask.assignee}</span></span>
                  </div>
                )}

                {user && selectedTask.assignee === (user.profile?.preferred_username || user.profile?.email) && (
                  <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    Assigned to me
                  </span>
                )}
              </div>
            </div>

            {/* Task Actions - Collapsible Section */}
            <div className="border-b border-gray-200">
              <button
                onClick={() => setShowTaskActions(!showTaskActions)}
                className="w-full px-6 py-3 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Settings size={16} />
                  <span>Manage Task</span>
                </div>
                <ChevronDown size={16} className={`transform transition-transform ${showTaskActions ? 'rotate-180' : ''}`} />
              </button>

              {showTaskActions && (
                <div className="px-6 pb-4 space-y-4 bg-gray-50/50">
                  <div className="grid grid-cols-3 gap-4">
                    {/* Priority Selector */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">Priority</label>
                      <select
                        value={editPriority}
                        onChange={(e) => setEditPriority(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                      >
                        <option value={25}>Low</option>
                        <option value={50}>Medium</option>
                        <option value={75}>High</option>
                      </select>
                    </div>

                    {/* Due Date Picker */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">Due Date</label>
                      <input
                        type="datetime-local"
                        value={editDueDate}
                        onChange={(e) => setEditDueDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                      />
                    </div>

                    {/* Assignee Selector */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5">Assign To</label>
                      <select
                        value={editAssignee}
                        onChange={(e) => setEditAssignee(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                      >
                        <option value="">-- Unassigned --</option>
                        {orgUsers.map((u) => (
                          <option key={u.id} value={u.username}>
                            {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.username}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleUpdateTask}
                      disabled={savingTask}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {savingTask ? <Loader2 size={14} className="animate-spin" /> : null}
                      Save Changes
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Dialog Content - Full Width Form */}
            <div className="flex-1 overflow-y-auto bg-white p-0">
              <div className="max-w-4xl mx-auto p-8">
                {/* Form Section */}
                <div>

                  {formLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 size={32} className="animate-spin text-green-600" />
                      <span className="ml-3 text-gray-600">Loading form...</span>
                    </div>
                  ) : isFormioForm && formSchema ? (
                    <div className="formio-form-wrapper bg-gray-50 rounded-lg p-4">
                      <FormioRenderer
                        form={formSchema}
                        submission={formSubmission}
                        onSubmit={(submission) => {
                          handleFormioSubmit(submission.data as Record<string, unknown>);
                        }}
                        options={{
                          readOnly: false,
                          noAlerts: true,
                        }}
                      />
                    </div>
                  ) : selectedTask?.formKey ? (
                    /* Default Camunda Form */
                    <form onSubmit={handleCompleteTask} className="space-y-4 bg-gray-50 rounded-lg p-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Username<span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="e.g., alex.chen"
                            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email address<span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="email"
                            placeholder="e.g., alex.chen@company.com"
                            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">User role</label>
                        <select
                          value={decision}
                          onChange={(e) => setDecision(e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none bg-white"
                        >
                          <option value="">Select</option>
                          <option value="admin">Admin</option>
                          <option value="user">User</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      </div>
                    </form>
                  ) : (
                    /* No Form Available */
                    <div className="text-center py-12">
                      <FileText size={64} className="mx-auto text-gray-300 mb-6" />
                      <h3 className="text-xl font-medium text-gray-900 mb-3">No Form Available</h3>
                      <p className="text-base text-gray-500 mb-6 max-w-md mx-auto">
                        This task doesn't have a form associated with it. You can complete the task by adding any necessary variables and clicking the Complete Task button.
                      </p>
                      <div className="text-sm text-gray-400">
                        Context variables are available in the Task Context section on the left.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Dialog Footer */}
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3 bg-white">
                <button
                  type="button"
                  onClick={() => setShowFullDialog(false)}
                  className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                {!selectedTask.assignee && (
                  <button
                    type="button"
                    onClick={handleClaimTask}
                    disabled={claiming}
                    className="flex items-center justify-center gap-2 px-6 py-2.5 border border-green-600 text-green-600 rounded-lg text-sm font-medium hover:bg-green-50 disabled:opacity-50 transition-colors"
                  >
                    {claiming ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : null}
                    Claim Task
                  </button>
                )}
                {/* Complete button - triggers Formio submit if form present, otherwise regular complete */}
                <button
                  type="button"
                  onClick={handleCompleteWithFormio}
                  disabled={completing}
                  className="flex items-center justify-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors shadow-sm"
                >
                  {completing ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : null}
                  Complete Task
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
